import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  formatDateTimeLabel,
  getDefaultPhoneCountry,
  getEditablePhoneSeed,
  getPublicBookingPhoneVerificationErrorMessage,
  type PhoneVerificationFeedbackTone,
} from '@/components/public/visits/publicBookingPhoneVerificationUx';
import { PhoneUtils, type PhoneCode, type PhoneValidationResult } from '@/lib/phone-codes';
import { authenticatedBookingReadinessKeys } from '@/hooks/public/visits/useAuthenticatedBookingReadiness';
import {
  publicBookingPhoneVerificationStateKeys,
  usePublicBookingPhoneVerificationState,
} from '@/hooks/public/visits/usePublicBookingPhoneVerificationState';
import {
  requestPublicBookingPhoneVerificationDelivery,
  resendPublicBookingPhoneVerificationDelivery,
  verifyPublicBookingPhoneVerification,
} from '@/services/publicBookingPhoneVerification';

export interface PhoneVerificationFeedback {
  tone: PhoneVerificationFeedbackTone;
  message: string;
}

export interface UsePublicBookingPhoneVerificationFlowOptions {
  enabled?: boolean;
  userId?: string | null;
  onVerified?: () => Promise<void> | void;
}

export interface PublicBookingPhoneVerificationFlowController {
  phone: string;
  selectedCountry: PhoneCode;
  phoneValidation: PhoneValidationResult;
  otpCode: string;
  feedback: PhoneVerificationFeedback | null;
  state: ReturnType<typeof usePublicBookingPhoneVerificationState>['data'];
  stateQuery: ReturnType<typeof usePublicBookingPhoneVerificationState>;
  maskedDeliveryTarget: string | null;
  resendAvailableLabel: string | null;
  rateLimitResetLabel: string | null;
  resendCountdownSeconds: number;
  canResend: boolean;
  isBusy: boolean;
  isPhoneValid: boolean;
  hasPendingPhone: boolean;
  hasVerifiedPhone: boolean;
  hasOpenChallenge: boolean;
  hasActiveOtpChallenge: boolean;
  canSubmitPhone: boolean;
  canSubmitOtp: boolean;
  verificationStage: 'phone' | 'otp' | 'success';
  requestMutationPending: boolean;
  resendMutationPending: boolean;
  verifyMutationPending: boolean;
  setPhone: (value: string) => void;
  setSelectedCountry: (country: PhoneCode) => void;
  setOtpCode: (value: string) => void;
  clearFeedback: () => void;
  submitPhone: () => Promise<void>;
  resendCode: () => Promise<void>;
  verifyCode: (code?: string) => Promise<void>;
}

function getCountdownSeconds(target?: string | null): number {
  if (!target) return 0;

  const targetTime = new Date(target).getTime();
  if (Number.isNaN(targetTime)) return 0;

  const diffSeconds = Math.ceil((targetTime - Date.now()) / 1000);
  return diffSeconds > 0 ? diffSeconds : 0;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function usePublicBookingPhoneVerificationFlow(
  options: UsePublicBookingPhoneVerificationFlowOptions = {},
): PublicBookingPhoneVerificationFlowController {
  const queryClient = useQueryClient();
  const stateQuery = usePublicBookingPhoneVerificationState({
    enabled: options.enabled ?? true,
    userId: options.userId,
  });
  const state = stateQuery.data;
  const [phone, setPhoneState] = useState('');
  const [selectedCountry, setSelectedCountryState] = useState<PhoneCode>(getDefaultPhoneCountry());
  const [otpCode, setOtpCodeState] = useState('');
  const [feedback, setFeedback] = useState<PhoneVerificationFeedback | null>(null);
  const [resendCountdownSeconds, setResendCountdownSeconds] = useState(0);
  const [showSuccessState, setShowSuccessState] = useState(false);
  const lastSeedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    lastSeedKeyRef.current = null;
    setPhoneState('');
    setSelectedCountryState(getDefaultPhoneCountry());
  }, [options.userId]);

  useEffect(() => {
    const seedSource = state?.securityPhonePending ?? state?.profilePhone ?? state?.securityPhoneVerified ?? null;
    const seedKey = options.userId ? `${options.userId}:${seedSource ?? 'empty'}` : seedSource ?? 'empty';

    if (lastSeedKeyRef.current === seedKey) {
      return;
    }

    const seed = getEditablePhoneSeed(seedSource);

    lastSeedKeyRef.current = seedKey;
    setSelectedCountryState(seed.country);
    setPhoneState(seed.localPhone);
  }, [options.userId, state?.profilePhone, state?.securityPhonePending, state?.securityPhoneVerified]);

  useEffect(() => {
    const syncCountdown = () => {
      setResendCountdownSeconds(getCountdownSeconds(state?.openChallengeNextSendAllowedAt));
    };

    syncCountdown();
    if (!state?.openChallengeNextSendAllowedAt) return undefined;

    const timer = window.setInterval(syncCountdown, 1000);
    return () => window.clearInterval(timer);
  }, [state?.openChallengeNextSendAllowedAt]);

  const maskedDeliveryTarget = state?.challengeDeliveryTargetMasked ?? state?.securityPhonePendingMasked ?? null;
  const resendAvailableLabel = formatDateTimeLabel(state?.openChallengeNextSendAllowedAt);
  const rateLimitResetLabel = formatDateTimeLabel(state?.openChallengeRateLimitResetAt);
  const phoneValidation = PhoneUtils.validatePhoneNumber(phone, selectedCountry.code);
  const normalizedPhone = phoneValidation.isValid ? phoneValidation.normalizedNumber : '';
  const isPhoneValid = phoneValidation.isValid;
  const hasPendingPhone = Boolean(state?.hasPendingSecurityPhone);
  const hasVerifiedPhone = Boolean(state?.hasVerifiedSecurityPhone);
  const hasOpenChallenge = Boolean(state?.openChallengePublicId);
  const hasActiveOtpChallenge = hasPendingPhone && hasOpenChallenge && Boolean(state?.canVerifyPending);

  const invalidateRelatedQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: publicBookingPhoneVerificationStateKeys.all }),
      queryClient.invalidateQueries({ queryKey: authenticatedBookingReadinessKeys.all }),
    ]);
  };

  const setPhone = (value: string) => {
    setPhoneState(value);
    setFeedback(null);
  };

  const setSelectedCountry = (country: PhoneCode) => {
    setSelectedCountryState(country);
    setFeedback(null);
  };

  const setOtpCode = (value: string) => {
    setOtpCodeState(value.replace(/\D+/g, '').slice(0, 6));
    setFeedback((current) => (current?.tone === 'error' ? null : current));
  };

  const requestMutation = useMutation({
    mutationFn: requestPublicBookingPhoneVerificationDelivery,
    onSuccess: async (result) => {
      setFeedback({
        tone: 'info',
        message: `Enviamos un código de 6 dígitos a ${result.deliveryTargetMasked ?? result.pendingPhoneMasked ?? 'tu WhatsApp'}.`,
      });
      setOtpCodeState('');
      setShowSuccessState(false);
      await invalidateRelatedQueries();
      toast.success('Código enviado', {
        description: 'Ya puedes confirmar tu identidad con el código que enviamos por WhatsApp.',
      });
    },
    onError: (error) => {
      const message = getPublicBookingPhoneVerificationErrorMessage(error);
      setFeedback({ tone: 'error', message });
      toast.error('No pudimos enviar el código', { description: message });
    },
  });

  const resendMutation = useMutation({
    mutationFn: resendPublicBookingPhoneVerificationDelivery,
    onSuccess: async (result) => {
      setFeedback({
        tone: 'info',
        message: `Reenviamos el código a ${result.deliveryTargetMasked ?? result.pendingPhoneMasked ?? 'tu WhatsApp'}.`,
      });
      setOtpCodeState('');
      setShowSuccessState(false);
      await invalidateRelatedQueries();
      toast.success('Código reenviado', {
        description: 'Revisa tu WhatsApp. Si tarda un poco, puedes intentar de nuevo más adelante.',
      });
    },
    onError: (error) => {
      const message = getPublicBookingPhoneVerificationErrorMessage(error);
      setFeedback({ tone: 'error', message });
      toast.error('No pudimos reenviar el código', { description: message });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: verifyPublicBookingPhoneVerification,
    onSuccess: async (result) => {
      if (result.wasVerifiedNow || result.status === 'verified') {
        setFeedback({
          tone: 'success',
          message: `Tu cuenta quedó confirmada con ${result.securityPhoneVerifiedMasked ?? 'tu teléfono verificado'}.`,
        });
        setShowSuccessState(true);
        setOtpCodeState('');
        await invalidateRelatedQueries();
        toast.success('Identidad confirmada', {
          description: 'Tu cuenta ya está lista para continuar con la reserva.',
        });
        await delay(1200);
        await options.onVerified?.();
        return;
      }

      setShowSuccessState(false);

      if (result.status === 'cancelled' || result.remainingAttempts <= 0) {
        setOtpCodeState('');
        setFeedback({
          tone: 'error',
          message: 'Ese código ya no se puede usar. Te ayudamos solicitando uno nuevo para continuar.',
        });
        return;
      }

      setOtpCodeState('');
      setFeedback({
        tone: 'error',
        message: `Ese código no coincide. Te quedan ${result.remainingAttempts} intento(s).`,
      });
    },
    onError: (error) => {
      const message = getPublicBookingPhoneVerificationErrorMessage(error);
      setShowSuccessState(false);
      setFeedback({ tone: 'error', message });
      toast.error('No pudimos confirmar el código', { description: message });
    },
  });

  const submitPhone = async () => {
    if (!isPhoneValid) {
      setFeedback({ tone: 'error', message: 'Escribe un número válido con su lada para continuar.' });
      return;
    }

    setFeedback(null);
    await requestMutation.mutateAsync(normalizedPhone);
  };

  const resendCode = async () => {
    setFeedback(null);
    await resendMutation.mutateAsync();
  };

  const verifyCode = async (code = otpCode) => {
    const normalizedCode = code.replace(/\D+/g, '').slice(0, 6);
    if (normalizedCode.length !== 6) {
      setFeedback({ tone: 'error', message: 'Ingresa los 6 dígitos completos para confirmar tu identidad.' });
      return;
    }

    setFeedback(null);
    await verifyMutation.mutateAsync(normalizedCode);
  };

  const isBusy = requestMutation.isPending || resendMutation.isPending || verifyMutation.isPending;
  const verificationStage = useMemo<'phone' | 'otp' | 'success'>(() => {
    if (showSuccessState || hasVerifiedPhone) return 'success';
    if (hasActiveOtpChallenge) return 'otp';
    return 'phone';
  }, [hasActiveOtpChallenge, hasVerifiedPhone, showSuccessState]);

  return {
    phone,
    selectedCountry,
    phoneValidation,
    otpCode,
    feedback,
    state,
    stateQuery,
    maskedDeliveryTarget,
    resendAvailableLabel,
    rateLimitResetLabel,
    resendCountdownSeconds,
    canResend: resendCountdownSeconds <= 0,
    isBusy,
    isPhoneValid,
    hasPendingPhone,
    hasVerifiedPhone,
    hasOpenChallenge,
    hasActiveOtpChallenge,
    canSubmitPhone: isPhoneValid && !isBusy,
    canSubmitOtp: otpCode.replace(/\D+/g, '').length === 6 && !isBusy,
    verificationStage,
    requestMutationPending: requestMutation.isPending,
    resendMutationPending: resendMutation.isPending,
    verifyMutationPending: verifyMutation.isPending,
    setPhone,
    setSelectedCountry,
    setOtpCode,
    clearFeedback: () => setFeedback(null),
    submitPhone,
    resendCode,
    verifyCode,
  };
}